import { useMutation, useQuery } from '@tanstack/react-query'
import { apiErrorFromResponse, authFetch } from '@/api/client'
import { ApiError, type ProblemDetail } from '@/lib/errors'

export type ImageProvider = 'pollinations' | 'hf'
export type ImageAspectRatio = 'shorts' | 'video' | 'avatar'

export interface ImageStyleMeta {
  key: string
  label: string
  description: string
}

export interface ImageModelMeta {
  key: string
  label: string
  description: string
  avg_latency_s: number
}

export interface ModelsByProvider {
  hf: ImageModelMeta[]
  pollinations: ImageModelMeta[]
}

export interface GenerateImageInput {
  prompt: string
  provider: ImageProvider
  style: string
  aspectRatio: ImageAspectRatio
  /** Provider-specific identifier; empty falls back to provider default on the backend. */
  model?: string
  negativePrompt?: string
  /** Integer seed for reproducibility; omit for a random one. */
  seed?: number | null
}

export interface GeneratedImage {
  blob: Blob
  url: string
}

const STYLES_QK = ['tools', 'image-generation', 'styles'] as const
const MODELS_QK = ['tools', 'image-generation', 'models'] as const

/**
 * Style registry — used by the visual picker. Cached for the session
 * since the list is static (lives in code, not the DB).
 */
export function useImageStyles() {
  return useQuery({
    queryKey: STYLES_QK,
    queryFn: async (): Promise<ImageStyleMeta[]> => {
      const res = await authFetch('/api/v1/tools/image-generation/styles', {
        credentials: 'same-origin',
      })
      if (!res.ok) throw await apiErrorFromResponse(res, 'Falha ao carregar estilos')
      const body = (await res.json()) as { data: ImageStyleMeta[] }
      return body.data
    },
    staleTime: Number.POSITIVE_INFINITY,
  })
}

export function useImageModels() {
  return useQuery({
    queryKey: MODELS_QK,
    queryFn: async (): Promise<ModelsByProvider> => {
      const res = await authFetch('/api/v1/tools/image-generation/models', {
        credentials: 'same-origin',
      })
      if (!res.ok) throw await apiErrorFromResponse(res, 'Falha ao carregar modelos')
      const body = (await res.json()) as { data: ModelsByProvider }
      return body.data
    },
    staleTime: Number.POSITIVE_INFINITY,
  })
}

/**
 * POC tool: generates an image from a text prompt. The backend returns
 * raw image bytes, so we bypass openapi-fetch (JSON-only) and use plain
 * fetch + ApiError to keep error semantics consistent with the rest of
 * the app.
 *
 * Caller is responsible for ``URL.revokeObjectURL(url)`` once the image
 * is no longer rendered, otherwise the blob URL leaks.
 */
export function useGenerateImage() {
  return useMutation({
    mutationFn: async (input: GenerateImageInput): Promise<GeneratedImage> => {
      const res = await authFetch('/api/v1/tools/image-generation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: input.prompt,
          provider: input.provider,
          style: input.style,
          aspect_ratio: input.aspectRatio,
          model: input.model ?? '',
          negative_prompt: input.negativePrompt ?? '',
          seed: input.seed ?? null,
        }),
        credentials: 'same-origin',
      })

      if (!res.ok) {
        let problem: ProblemDetail | null = null
        const contentType = res.headers.get('content-type') ?? ''
        if (contentType.includes('application/json') || contentType.includes('application/problem+json')) {
          try {
            // `| null`: o cast puro afirmava que o corpo sempre é um objeto, e
            // o eslint marcava `body &&` como sempre verdadeiro. `res.json()`
            // devolve `null` para um corpo literal "null", então a guarda é
            // real — o que estava errado era o tipo.
            const body = (await res.json()) as ProblemDetail | null
            if (body && typeof body.title === 'string') problem = body
          } catch {
            /* fall through */
          }
        }
        const message = problem?.detail ?? problem?.title ?? `Falha ao gerar imagem (HTTP ${String(res.status)})`
        throw new ApiError(message, res.status, problem, null)
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      return { blob, url }
    },
  })
}
